import { Location } from '../types/rides';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
let lastPickupLat = -1.2921, lastPickupLng = 36.8219;
const searchCache: Record<string, Location[]> = {};
let pendingRequest: AbortController | null = null;
let placesServiceInstance: any = null;

export function setPickupLocation(lat: number, lng: number) {
  lastPickupLat = lat; lastPickupLng = lng;
}

export function initPlacesService(svc: any) {
  placesServiceInstance = svc;
  // Bust stale cache so the next search goes through Google Places
  Object.keys(searchCache).forEach(k => delete searchCache[k]);
}

function getDist(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371, dLat = (lat2-lat1)*Math.PI/180, dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R*2*Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function detectCategory(name: string, address: string): { icon: string; type: string } {
  const text = (name + ' ' + address).toLowerCase();
  if (/school|academy|college|university|institute|campus|primary|secondary|high school/i.test(text))
    return { icon: '🎓', type: 'School' };
  if (/hospital|clinic|medical|health|dispensary|pharmacy|doctor/i.test(text))
    return { icon: '🏥', type: 'Hospital' };
  if (/petrol|gas|fuel|oil|total|shell|kobil|rubis|engen|oilibya/i.test(text))
    return { icon: '⛽', type: 'Petrol Station' };
  if (/hotel|lodge|guest|airbnb|stay|inn|motel/i.test(text))
    return { icon: '🏨', type: 'Hotel' };
  if (/restaurant|cafe|coffee|eatery|food|grill|pizzeria|bake|kfc|java|artcaffe/i.test(text))
    return { icon: '🍽️', type: 'Restaurant' };
  if (/mall|shop|market|store|supermarket|centre|plaza|arcade|naivas|quickmart|carrefour/i.test(text))
    return { icon: '🛒', type: 'Shopping' };
  if (/church|mosque|temple|cathedral|shrine/i.test(text))
    return { icon: '⛪', type: 'Place of Worship' };
  if (/bank|atm|equity|kcb|cooperative|sacco|mpesa/i.test(text))
    return { icon: '🏦', type: 'Bank / ATM' };
  if (/police|station|prison|court|government|county|nyumba kumi/i.test(text))
    return { icon: '🏛️', type: 'Government' };
  if (/airport|airstrip|terminal|jkia|wilson/i.test(text))
    return { icon: '✈️', type: 'Airport' };
  if (/park|garden|stadium|sports|field|gym|fitness/i.test(text))
    return { icon: '🏟️', type: 'Recreation' };
  if (/estate|apartment|residential|housing|homes|court|close|road|avenue|street/i.test(text))
    return { icon: '🏘️', type: 'Residential' };
  if (/bus|stage|terminus|matatu|train|depot/i.test(text))
    return { icon: '🚌', type: 'Bus Stop' };
  return { icon: '📍', type: 'Location' };
}

// --- Google Places API (New) - primary ---
async function searchWithNewPlaces(query: string): Promise<Location[]> {
  const win = window as any;
  const places = win.google?.maps?.places;
  if (!places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) return [];

  try {
    const request: any = {
      input: query,
      includedRegionCodes: ['ke'],
      locationBias: {
        center: { lat: lastPickupLat, lng: lastPickupLng },
        radius: 40000,
      },
    };
    const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    if (!suggestions?.length) return [];
    return suggestions.slice(0, 7).map((s: any) => {
      const p = s.placePrediction;
      const main = p?.mainText?.text || p?.text?.text?.split(',')[0] || '';
      const second = p?.secondaryText?.text || '';
      const full = p?.text?.text || main;
      const cat = detectCategory(main, full);
      return {
        lat: 0, lng: 0,             // resolved lazily on selection
        address: full,
        name: main,
        placeId: p?.placeId,
        type: cat.type,
        icon: cat.icon,
        subtitle: second,
      } as any;
    });
  } catch (e: any) {
    console.warn('[SafariCab] New Places autocomplete failed:', e?.message || e);
    return [];
  }
}

// --- Legacy AutocompleteService - secondary (only if old Places API is enabled) ---
async function searchWithLegacyPlaces(query: string): Promise<Location[]> {
  const win = window as any;
  const svc = placesServiceInstance || (() => {
    const G = win.google?.maps?.places;
    return G?.AutocompleteService ? new G.AutocompleteService() : null;
  })();
  if (!svc) return [];

  return new Promise((resolve) => {
    const req: any = {
      input: query,
      componentRestrictions: { country: 'ke' },
      location: new win.google.maps.LatLng(lastPickupLat, lastPickupLng),
      radius: 40000,
    };
    svc.getPlacePredictions(req, (preds: any[], status: string) => {
      if (!preds || status !== 'OK') { resolve([]); return; }
      const results: Location[] = preds.slice(0, 7).map((p: any) => {
        const mainText = p.structured_formatting?.main_text || p.description.split(',')[0];
        const secondText = p.structured_formatting?.secondary_text || '';
        const cat = detectCategory(mainText, p.description);
        return { lat: 0, lng: 0, address: p.description, name: mainText, placeId: p.place_id, type: cat.type, icon: cat.icon, subtitle: secondText } as any;
      });
      resolve(results);
    });
  });
}

async function searchWithGooglePlaces(query: string): Promise<Location[]> {
  const fresh = await searchWithNewPlaces(query);
  if (fresh.length > 0) return fresh;
  return searchWithLegacyPlaces(query);
}

// --- Mapbox (fallback) ---
async function searchWithMapbox(query: string): Promise<Location[]> {
  if (pendingRequest) pendingRequest.abort();
  pendingRequest = new AbortController();
  const results: Location[] = [];
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=KE&limit=8&types=address,poi,place,locality&proximity=${lastPickupLng},${lastPickupLat}&fuzzyMatch=true`;
    const res = await fetch(url, { signal: pendingRequest.signal });
    const data = await res.json();
    if (data.features) {
      for (const f of data.features) {
        const cat = detectCategory(f.text, f.place_name);
        results.push({
          lat: f.center[1], lng: f.center[0],
          address: f.place_name, name: f.text,
          type: cat.type, icon: cat.icon,
          distance: getDist(lastPickupLat, lastPickupLng, f.center[1], f.center[0]),
        });
      }
    }
  } catch {}

  if (results.length < 3) {
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=ke&limit=5&addressdetails=1`;
      const res = await fetch(url);
      const data = await res.json();
      for (const item of data) {
        const cat = detectCategory(item.name || '', item.display_name);
        results.push({
          lat: parseFloat(item.lat), lng: parseFloat(item.lon),
          address: item.display_name?.split(',').slice(0, 3).join(',') || item.display_name,
          name: item.name || item.display_name?.split(',')[0],
          type: cat.type, icon: cat.icon,
        });
      }
    } catch {}
  }

  results.sort((a: any, b: any) => (a.distance || 999) - (b.distance || 999));
  return results.filter((r, i, arr) => arr.findIndex(t => t.address === r.address) === i).slice(0, 6);
}

export async function searchLocation(query: string): Promise<Location[]> {
  if (!query || query.length < 2) return [];
  const key = query.toLowerCase().trim();
  if (searchCache[key]) return searchCache[key];

  // Google Places: fuzzy matching + full POI coverage (petrol, malls, buildings…)
  const win = window as any;
  if (placesServiceInstance || win.google?.maps?.places?.AutocompleteService) {
    const results = await searchWithGooglePlaces(query);
    if (results.length > 0) {
      searchCache[key] = results;
      setTimeout(() => { delete searchCache[key]; }, 60000);
      return results;
    }
  }

  // Fallback: Mapbox + Nominatim
  const results = await searchWithMapbox(query);
  searchCache[key] = results;
  setTimeout(() => { delete searchCache[key]; }, 60000);
  return results;
}

// Resolve a placeId to real coordinates (called lazily when user selects a suggestion)
export async function resolvePlaceId(placeId: string): Promise<{ lat: number; lng: number; address: string } | null> {
  const win = window as any;
  const places = win.google?.maps?.places;

  // New Places API
  if (places?.Place) {
    try {
      const place = new places.Place({ id: placeId });
      await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName'] });
      if (place.location) {
        const displayName: string = place.displayName || '';
        const formatted: string = place.formattedAddress || displayName;
        // Prepend the human name (e.g. "Jamia Mosque") if Google's formatted
        // address starts with a street number instead of the place name, so
        // the driver's split(',')[0] shows the right destination.
        const address = displayName && !formatted.toLowerCase().startsWith(displayName.toLowerCase())
          ? `${displayName}, ${formatted}`
          : formatted;
        return { lat: place.location.lat(), lng: place.location.lng(), address };
      }
    } catch (e: any) {
      console.warn('[SafariCab] New Places fetchFields failed:', e?.message || e);
    }
  }

  // Legacy geocoder fallback
  if (win.google?.maps?.Geocoder) {
    return new Promise((resolve) => {
      const geocoder = new win.google.maps.Geocoder();
      geocoder.geocode({ placeId }, (results: any[], status: string) => {
        if (status === 'OK' && results[0]) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            address: results[0].formatted_address,
          });
        } else { resolve(null); }
      });
    });
  }
  return null;
}

export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.display_name) return data.display_name.split(',').slice(0, 4).join(', ');
  } catch {}
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}
